"use client"
import { useAuth } from "@/components/auth-provider"
import { Button } from "@/components/ui/button"
import { LanguageSwitcher } from "@/components/language-switcher"
import { useTranslations } from "next-intl"
import Link from "next/link"
import { ReactNode, useState } from "react"
import { Menu, X } from "lucide-react"

export default function MainLayout({ children }: { children: ReactNode }) {
	const user = useAuth()
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
	const t = useTranslations("home.navigation")
	const footer = useTranslations("home.footer")

	return (
		<div className="min-h-dvh flex flex-col bg-gradient-to-br from-blue-50 to-indigo-100">
			<header className="bg-white shadow-sm border-b">
				<div className="container mx-auto px-4 py-4">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-2">
							<Link href="/" aria-label={t("home")}>
								<img
									src="/qaderoon-full.png"
									alt="Qaderoon"
									width={128}
									height={128}
								/>
							</Link>

							<img
								src="/hrsd_logo.svg"
								alt="HSRD"
								className=""
								width={128}
								height={128}
							/>
						</div>

						<nav className="hidden lg:flex lg:items-center lg:gap-6">
							<Link
								href="/"
								className="text-gray-700 hover:text-main transition-colors"
							>
								{t("home")}
							</Link>
							<Link
								href="/#about"
								className="text-gray-700 hover:text-main transition-colors"
							>
								{t("about")}
							</Link>
							<Link
								href="/#services"
								className="text-gray-700 hover:text-main transition-colors"
							>
								{t("services")}
							</Link>
							<Link
								href="/#contact"
								className="text-gray-700 hover:text-main transition-colors"
							>
								{t("contact")}
							</Link>

							{/* Language Switcher */}
							<LanguageSwitcher variant="desktop" />

							{user ? (
								<div className="flex items-center gap-4">
									{["employee", "organization"].includes(
										user.type
									) ? (
										<Link
											href="/account"
											className="text-gray-700 hover:text-main transition-colors"
										>
											{t("profile")}
										</Link>
									) : (
										<Link
											href="/panel"
											className="text-gray-700 hover:text-main transition-colors"
										>
											{t("dashboard")}
										</Link>
									)}

									<a href="/logout">
										<Button>{t("logout")}</Button>
									</a>
								</div>
							) : (
								<Link href="/login" aria-label={t("join_now")}>
									<Button>{t("join_now")}</Button>
								</Link>
							)}
						</nav>

						<div className="lg:hidden">
							<Button
								variant="ghost"
								size="sm"
								className="p-2"
								aria-label={t("open_menu")}
								onClick={() => setIsMobileMenuOpen(true)}
							>
								<Menu className="h-6 w-6" />
							</Button>
						</div>
					</div>
				</div>
			</header>

			{/* Mobile Menu Modal */}
			{isMobileMenuOpen && (
				<div className="lg:hidden fixed inset-0 z-50">
					{/* Modal Backdrop */}
					<div
						className="fixed inset-0 bg-black/50 backdrop-blur-sm"
						onClick={() => setIsMobileMenuOpen(false)}
					/>

					{/* Modal Content */}
					<div className="fixed inset-0 flex items-center justify-center p-4">
						<div className="w-full max-w-md bg-white rounded-lg shadow-xl max-h-[80vh] overflow-hidden">
							<div className="flex flex-col h-full">
								{/* Modal Header */}
								<div className="flex items-center justify-between p-4 border-b">
									<h2 className="text-lg font-semibold">
										{t("menu")}
									</h2>
									<Button
										variant="ghost"
										size="sm"
										onClick={() =>
											setIsMobileMenuOpen(false)
										}
										className="p-2"
									>
										<X className="h-4 w-4" />
									</Button>
								</div>

								{/* Modal Body */}
								<div className="flex-1 overflow-y-auto p-4">
									<nav className="flex flex-col space-y-3">
										<Link
											href="/"
											className="text-gray-700 hover:text-main transition-colors py-2 px-3 rounded-md hover:bg-gray-50"
											onClick={() =>
												setIsMobileMenuOpen(false)
											}
										>
											{t("home")}
										</Link>
										<Link
											href="/#about"
											className="text-gray-700 hover:text-main transition-colors py-2 px-3 rounded-md hover:bg-gray-50"
											onClick={() =>
												setIsMobileMenuOpen(false)
											}
										>
											{t("about")}
										</Link>
										<Link
											href="/#services"
											className="text-gray-700 hover:text-main transition-colors py-2 px-3 rounded-md hover:bg-gray-50"
											onClick={() =>
												setIsMobileMenuOpen(false)
											}
										>
											{t("services")}
										</Link>
										<Link
											href="/#contact"
											className="text-gray-700 hover:text-main transition-colors py-2 px-3 rounded-md hover:bg-gray-50"
											onClick={() =>
												setIsMobileMenuOpen(false)
											}
										>
											{t("contact")}
										</Link>

										{/* Language Switcher for Mobile */}
										<LanguageSwitcher variant="mobile" />

										{user ? (
											<>
												<div className="border-t pt-3 mt-3">
													{[
														"employee",
														"organization",
													].includes(user.type) ? (
														<Link
															href="/account"
															className="text-gray-700 hover:text-main transition-colors py-2 px-3 rounded-md hover:bg-gray-50 block"
															onClick={() =>
																setIsMobileMenuOpen(
																	false
																)
															}
														>
															{t("profile")}
														</Link>
													) : (
														<Link
															href="/panel"
															className="text-gray-700 hover:text-main transition-colors py-2 px-3 rounded-md hover:bg-gray-50 block"
															onClick={() =>
																setIsMobileMenuOpen(
																	false
																)
															}
														>
															{t("dashboard")}
														</Link>
													)}
												</div>
												<div className="pt-3">
													<a
														href="/logout"
														className="block"
													>
														<Button className="w-full">
															{t("logout")}
														</Button>
													</a>
												</div>
											</>
										) : (
											<div className="pt-3">
												<Link
													href="/login"
													aria-label={t("join_now")}
													onClick={() =>
														setIsMobileMenuOpen(
															false
														)
													}
												>
													<Button className="w-full">
														{t("join_now")}
													</Button>
												</Link>
											</div>
										)}
									</nav>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}

			<main className="grow">{children}</main>

			<footer className="bg-lochmara-950 text-white py-12 px-4">
				<div className="container mx-auto">
					<div className="grid grid-cols-1 md:grid-cols-4 gap-8">
						<div>
							<div className="flex items-center gap-3 mb-4">
								<img
									src="/qaderoon_wide.png"
									alt="Qaderoon"
									className="max-w-40 w-full"
								/>
							</div>
							<p className="text-gray-300 font-medium text-sm">
								{footer("description")}
							</p>
						</div>

						<div>
							<h4 className="font-semibold mb-4">
								{footer("related_links_title")}
							</h4>
							<ul className="space-y-2 text-sm text-gray-300 font-medium">
								<li>
									<a
										href="https://www.qaderoon.sa"
										target="_blank"
										rel="noopener noreferrer"
										className="hover:text-white transition-colors"
									>
										{footer("qaderoon_website")}
									</a>
								</li>
								<li>
									<a
										href="https://hrsd.gov.sa/"
										target="_blank"
										rel="noopener noreferrer"
										className="hover:text-white transition-colors"
									>
										{footer("hrsd_ministry")}
									</a>
								</li>
								<li>
									<a
										href="https://www.hrdf.org.sa/Home"
										target="_blank"
										rel="noopener noreferrer"
										className="hover:text-white transition-colors"
									>
										{footer("hrdf_fund")}
									</a>
								</li>
							</ul>
						</div>

						<div>
							<h4 className="font-semibold mb-4">
								{footer("quick_links_title")}
							</h4>
							<ul className="space-y-2 text-sm text-gray-300 font-medium">
								<li>
									<Link
										href="/"
										className="hover:text-white transition-colors"
									>
										{footer("home")}
									</Link>
								</li>
								<li>
									<Link
										href="/jobs"
										className="hover:text-white transition-colors"
									>
										{footer("available_jobs")}
									</Link>
								</li>
								<li>
									<Link
										href="/#contact"
										className="hover:text-white transition-colors"
									>
										{footer("contact_form")}
									</Link>
								</li>
								<li>
									<Link
										href="/terms-conditions"
										className="hover:text-white transition-colors"
									>
										{footer("terms_conditions")}
									</Link>
								</li>
								<li>
									<Link
										href="/privacy-policy"
										className="hover:text-white transition-colors"
									>
										{footer("privacy_policy")}
									</Link>
								</li>
							</ul>
						</div>

						<div>
							<h4 className="font-semibold mb-4">
								{footer("contact_us_title")}
							</h4>
							<ul className="space-y-2 text-sm text-gray-300 font-medium">
								<li>
									<span>{footer("email_label")}: </span>
									<a
										href="mailto:recruitment@qaderoon.sa"
										className="hover:text-white transition-colors"
									>
										recruitment@qaderoon.sa
									</a>
								</li>
								<li>
									<span>{footer("mobile_label")}: </span>
									<a
										href="tel:0539186004"
										className="hover:text-white transition-colors"
									>
										0539186004
									</a>
								</li>
								<li>
									<span>{footer("address_line1")}</span>
								</li>
								<li>
									<span>{footer("address_line2")}</span>
								</li>
							</ul>
						</div>
					</div>

					<div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm text-gray-300 font-medium space-y-2">
						<p>
							&copy; {new Date().getFullYear()}{" "}
							{footer("copyright")}
						</p>
						<p className="text-white">
							{footer("made_with")}{" "}
							<a
								href="https://expertivia.net/?utm_source=qaderoon"
								target="_blank"
								rel="noopener noreferrer"
								className="transition-colors underline"
							>
								{footer("expertivia")}
							</a>
						</p>
					</div>
				</div>
			</footer>
		</div>
	)
}
